import React, {FC, Fragment, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {NavLink} from "react-router-dom";
import Highlighter from "react-highlight-words";
import useDebouncedEffect from "use-debounced-effect";

import {CompanyInfoTooltip, LazyLoadImageWrapper} from "components";
import {sanitizeStringText} from "helpers/map";
import {LineIcon, PlusIcon} from "assets/icons";
import {useAppDispatch, useAppSelector} from "hooks";
import useUserBehaviorTrackHook from "hooks/useUserBehaviorTrackHook";
import {setTrackedEventsData} from "store/events/eventTrackingActions";
import {tooltipCompaniesSelector} from "store/company/companySelectors";
import {updateTooltipCompaniesList} from "store/company/companyAsyncActions";
import {candidateSelectedKeywordLabelsSelector} from "store/candidate/candidateSelectors";
import {apCandidateSelectedKeywordLabelsSelector} from "store/additional-profiles/additionalProfilesSelectors";

import {ICandidateGroupedExperience, TCandidateExperienceProps} from "./types";
import styles from "./candidateExperience.module.scss";

import type {SICandidatePosition} from "api/candidateApi/types";
import DealExperienceIcon from "assets/icons/DealExperienceIcon";
import DealExperienceToolTip from "components/Tooltip/DealExperienceTooltip";
import {STCompanyEvent} from "api/companyApi/types";
import DealExperienceInfo from "components/Other/DealExperience/dealExperienceInfo";
import classNames from "classnames";
import {CandidateCareerHistory} from "helpers/CandidateCareerHistory";

const CandidateExperience: FC<TCandidateExperienceProps> = ({
	searchType,
	position,
	candidateId,
	isRecommended,
	fromQuickView = false,
	displayDealExperience,
	positions,
}) => {
	const dispatch = useAppDispatch();

	const keywordLabels = useAppSelector(candidateSelectedKeywordLabelsSelector);
	const apKeywordLabels = useAppSelector(apCandidateSelectedKeywordLabelsSelector);

	const [companyId, setCompanyId] = useState<string>("");
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [hoveredCompanyId, setHoveredCompanyId] = useState<string | null>(null);
	const [eventRecordedForRole, setEventRecordedForRole] = useState<boolean>(false);

	const {itemEventDetails} = useUserBehaviorTrackHook({
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		position,
		source: searchType,
		isRecommended,
		itemId: candidateId,
		itemType: "candidate",
		defaultEvent: "expanded",
	});

	const tooltipId = `${searchType}-${candidateId}`;
	const tooltipIdDE = `${searchType}-${candidateId}-DE`;
	const tooltipCompanies = useAppSelector(tooltipCompaniesSelector);
	const companyToWatch = companyId ? tooltipCompanies[companyId] : null;

	const isAIResultsType = useMemo(() => {
		return (
			searchType === "Additional Profiles" ||
			searchType === "Free Text Input Search" ||
			searchType === "More Like This" ||
			searchType === "Rejected Profiles"
		);
	}, [searchType]);

	const shouldSearchHighlight = useMemo(() => isAIResultsType, [isAIResultsType]);

	const groupedExperiencesByCompany = useMemo<ICandidateGroupedExperience[]>(
		() => CandidateCareerHistory(positions),
		[positions],
	);

	// Check if there is at least one description or event in the positions
	const hasDescriptionsOrEvents = useMemo(() => {
		return positions?.some(
			(val: SICandidatePosition) =>
				val.description ||
				(Array.isArray(val?.company?.events) && val?.company?.events.length > 0),
		);
	}, [positions]);

	useDebouncedEffect(
		() => {
			if (companyId && hoveredCompanyId === companyId) {
				dispatch(updateTooltipCompaniesList(companyId));
			}
		},
		1000,
		[companyId, hoveredCompanyId],
	);

	const [eventToDisplay, setEventToDisplay] = useState<STCompanyEvent[]>([]);
	const hoverTimer = useRef<NodeJS.Timeout | null>(null);

	const handleMouseEnter = (company: SICandidatePosition["company"]) => {
		hoverTimer.current = setTimeout(() => {
			setHoveredCompanyId(String(company.id));
			setCompanyId(String(company.id));

			if (company?.events && company?.events?.length > 0) {
				setEventToDisplay(company?.events);
			}
		}, 800);
	};

	const handleCompanyMouseLeave = () => {
		if (hoverTimer.current) {
			clearTimeout(hoverTimer.current);
			hoverTimer.current = null;
			setCompanyId("");
		}
	};

	const handleMouseLeave = () => {
		setEventToDisplay([]);
		handleCompanyMouseLeave();
	};

	// Clear timeout on unmounting to prevent any lingering async calls.
	useEffect(() => {
		return () => {
			if (hoverTimer.current) {
				clearTimeout(hoverTimer.current);
				setCompanyId("");
			}
		};
	}, []);

	const getJobTitleWithHighlighted = useCallback(
		(description: string) => {
			const allowed = shouldSearchHighlight || fromQuickView;
			const additionalOrNot = isAIResultsType ? apKeywordLabels : keywordLabels;

			if (additionalOrNot && allowed) {
				return (
					<Highlighter
						autoEscape={false}
						searchWords={additionalOrNot}
						textToHighlight={description}
						highlightClassName={styles.highlighter}
					/>
				);
			} else {
				return description;
			}
		},
		[shouldSearchHighlight, fromQuickView, isAIResultsType, apKeywordLabels, keywordLabels],
	);

	const getPositionEndDate = (date: Nullable<string>, isCurrent?: boolean | undefined) => {
		if (isCurrent !== undefined) {
			return isCurrent ? "Present" : date ? new Date(date).getFullYear() : "n/a";
		} else {
			return date ? new Date(date).getFullYear() : "Present";
		}
	};

	return (
		<Fragment>
			<div className={styles.workList}>
				<div
					className={classNames(styles.expandContainer, {
						[styles.hidden]: !(fromQuickView && hasDescriptionsOrEvents),
					})}
				>
					<button
						className={styles.expandButton}
						onClick={() => {
							setIsExpanded((prev) => !prev);
							if (!isExpanded) {
								if (!eventRecordedForRole) {
									dispatch(setTrackedEventsData(itemEventDetails));
									setEventRecordedForRole(true);
								}
							}
						}}
					>
						{isExpanded ? <LineIcon /> : <PlusIcon />}
						<span>
							{isExpanded ? "Collapse" : "Expand"} role
							{positions?.length === 1 ? "" : "s"}
						</span>
					</button>
				</div>
				{fromQuickView && !hasDescriptionsOrEvents && (
					<section>
						<br />
					</section>
				)}

				{[...groupedExperiencesByCompany].map((exp: ICandidateGroupedExperience, idx) => (
					<div key={idx} className={styles.experience}>
						<div className={styles.companyNameContainer}>
							<span className={styles.companyName}>
								<NavLink
									data-tooltip-id={tooltipId}
									data-tooltip-delay-show={300}
									onMouseEnter={() => handleMouseEnter(exp?.company)}
									// onMouseLeave={handleCompanyMouseLeave}
									target="_blank"
									rel="noreferrer"
									to={`/company/${exp.company.id}`}
								>
									{/*<div*/}
									{/*    className={styles.companyName__skeleton}*/}
									{/*    onMouseEnter={() => handleMouseEnter(exp?.company)}*/}
									{/*    onMouseLeave={handleCompanyMouseLeave}*/}
									{/*/>*/}
									<LazyLoadImageWrapper
										alt={exp && exp.company.name}
										image={
											exp.company.logo_url !== null
												? exp.company.logo_url
												: "https://storage.googleapis.com/nebula-static/logos100x100/empty_logo.png"
										}
										className={styles.companyLogo}
									/>
									{exp.company.name}
								</NavLink>
							</span>
							{exp.details?.some(
								(detail) => detail.events && detail.events.length > 0,
							) &&
								displayDealExperience &&
								!isExpanded && (
									<div
										onMouseEnter={() => handleMouseEnter(exp?.company)}
										onMouseLeave={handleMouseLeave}
										className={styles.iconContainer}
										data-tooltip-id={tooltipIdDE}
										data-tooltip-delay-show={300}
									>
										<DealExperienceIcon />
									</div>
								)}
						</div>

						<span className={styles.details}>
							{exp.details.map((detail, dx) => (
								<div key={`${idx}${dx}${detail.position_id}`}>
									<div className={styles.detail}>
										{detail.start_date ? (
											<span className={styles.years}>
												{`${new Date(
													detail.start_date,
												).getFullYear()} - ${getPositionEndDate(
													detail.end_date,
													detail.is_current,
												)}`}
											</span>
										) : (
											<span className={styles.years}>{`n/a - n/a`}</span>
										)}
										<span className={styles.jobTitle}>
											{getJobTitleWithHighlighted(detail.job_title)}
										</span>
									</div>
									{detail.description !== null && isExpanded && (
										<div className={styles.description}>
											{sanitizeStringText(detail.description)
												.split("<br/>")
												.map((chunk, index) => (
													<div key={index}>
														{index > 0 && (
															<span
																style={{
																	display: "flex",
																	paddingBottom: 5,
																}}
															/>
														)}
														{getJobTitleWithHighlighted(chunk)}
													</div>
												))}
										</div>
									)}
									{isExpanded &&
									detail?.events?.length &&
									detail.events.length > 0 ? (
										<div style={{margin: "15px 0 0 115px"}}>
											<DealExperienceInfo events={detail.events} />
										</div>
									) : (
										""
									)}
								</div>
							))}
						</span>
					</div>
				))}
			</div>

			<CompanyInfoTooltip id={tooltipId} company={companyToWatch} />

			<DealExperienceToolTip events={eventToDisplay} id={tooltipIdDE} />
		</Fragment>
	);
};

export default CandidateExperience;
